import { ErrorInfo, ReactNode } from "react";

// Base error boundary props that all error boundaries share
export interface BaseErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

// Common context properties that appear across multiple error boundaries
export interface CommonContextProps {
  // Device and organization context
  deviceId?: string;
  orgId?: string;
  userId?: string;
  deviceName?: string;
  deviceType?: string;
  isDemoDevice?: boolean;

  // Document context
  templateKey?: string | null;
  docId?: string;
  docTitle?: string;
  docVersion?: string;
  docStatus?: string;
  docType?: string;

  // Form context
  stepId?: string;
  stepTitle?: string;
  stepType?: string;
  totalSteps?: number;
  currentStep?: number;
  currentStepIndex?: number;
  isLastStep?: boolean;
  isFirstStep?: boolean;
  formStatus?: string;
  documentStatus?: string;

  // Assistant context
  assistantProcessId?: string;
  assistantStatus?: string;
  processType?: string;

  // Roadmap context
  totalStages?: number;
  completedStages?: number;
  totalCards?: number;
  completedCards?: number;
  currentStage?: string;
  subscriptionPlan?: string;

  // Task context
  cardKey?: string;
  stageKey?: string;
  cardTitle?: string;

  // Terminal context
  threadId?: string;

  // File context
  fileSize?: number;
  fileType?: string;
  fileName?: string;
  isUploading?: boolean;
  isUserUpload?: boolean;

  // Document viewer context
  isGenerating?: boolean;
  isEditing?: boolean;
  hasMultipleInstances?: boolean;
  isUnlocked?: boolean;
  viewerType?: string;
  docProgress?: string;
  isCreatingInstance?: boolean;
  rawEditorEnabled?: boolean;
}

// Specific context types for different error boundary categories
export interface DocumentViewerContext extends CommonContextProps {
  // Document-specific properties
  isUserUpload?: boolean;
  isGenerating?: boolean;
  isEditing?: boolean;
  hasMultipleInstances?: boolean;
  isUnlocked?: boolean;
  viewerType?: string;
  docProgress?: string;
  fileSize?: number;
  fileType?: string;
  fileName?: string;
  isUploading?: boolean;
}

export interface FormContext extends CommonContextProps {
  // Form-specific properties
  stepId?: string;
  stepTitle?: string;
  stepType?: string;
  totalSteps?: number;
  currentStep?: number;
  currentStepIndex?: number;
  isLastStep?: boolean;
  isFirstStep?: boolean;
  formStatus?: string;
  documentStatus?: string;
  assistantProcessId?: string;
  assistantStatus?: string;
  processType?: string;
  docVersionId?: string;
  tasks?: any;
  templatesConfig?: any;
  step?: any;
  steps?: any;
}

export interface RoadmapContext extends CommonContextProps {
  device?: any;
  tasks?: any;
  documents?: any;
  user?: any;
  subscription?: any;
  devices?: any;
  templatesConfig?: any;
  stages?: any;
  roadmap?: any;
  selectedCard?: any;
  setSelectedCard?: any;
  setSearchParams?: any;
  editDevice?: any;
  setEditDevice?: any;
  card?: any;
}

export interface TaskContext extends CommonContextProps {
  cardKey?: string;
  stageKey?: string;
  cardTitle?: string;
  card?: any;
  open?: boolean;
  props?: any;
}

export interface TerminalContext extends CommonContextProps {
  // Terminal-specific properties
  threadId?: string;
}

// File information type for file-related error boundaries
export interface FileInfo {
  size: number;
  type: string;
  name?: string;
}

// State data types for different error boundary categories
export interface DocumentViewerState {
  templateType?: string;
  documentStatus?: string;
  isGenerating?: boolean;
  isEditing?: boolean;
  hasMultipleInstances?: boolean;
  isUnlocked?: boolean;
  documentType?: string;
  version?: string;
  isCustom?: boolean;
  isUpload?: boolean;
  progressStatus?: string;
  viewerMode?: string;
  fileInfo?: FileInfo;
  isUploading?: boolean;
  isUserUpload?: boolean;
  isCreatingInstance?: boolean;
  rawEditorEnabled?: boolean;
}

export interface FormState {
  templateType?: string;
  currentStep?: number;
  currentStepIndex?: number;
  stepProgress?: string;
  stepPosition?: "first" | "middle" | "last";
  deviceType?: string;
  isDemo?: boolean;
  formStatus?: string;
  documentStatus?: string;
  subscriptionTier?: string;
  processStatus?: string;
  processType?: string;
}

export interface RoadmapState {
  completedStages?: number;
  completedCards?: number;
  deviceType?: string;
  isDemo?: boolean;
  currentStage?: string;
  subscriptionTier?: string;
  progressPercentage?: number;
  cardProgressPercentage?: number;
}

export interface TaskState {
  cardKey?: string;
  stageKey?: string;
  cardTitle?: string;
}

export interface TerminalState {
  threadId?: string;
  deviceId?: string;
  orgId?: string;
}

// Helper functions to create state data with proper typing
export const createDocumentViewerState = (
  context: DocumentViewerContext
): DocumentViewerState => ({
  templateType: context.templateKey ?? undefined,
  documentStatus: context.docStatus,
  isGenerating: context.isGenerating,
  isEditing: context.isEditing,
  hasMultipleInstances: context.hasMultipleInstances,
  isUnlocked: context.isUnlocked,
  documentType: context.docType,
  version: context.docVersion,
  isUpload: context.isUserUpload,
  progressStatus: context.docProgress,
  viewerMode: context.viewerType,
  fileInfo:
    context.fileSize && context.fileType
      ? {
          size: context.fileSize,
          type: context.fileType,
          name: context.fileName,
        }
      : undefined,
  isUploading: context.isUploading,
  isUserUpload: context.isUserUpload,
  isCreatingInstance: context.isCreatingInstance,
  rawEditorEnabled: context.rawEditorEnabled,
});

export const createFormState = (context: FormContext): FormState => ({
  templateType: context.templateKey ?? undefined,
  currentStep: context.currentStep,
  currentStepIndex: context.currentStepIndex,
  deviceType: context.deviceType,
  isDemo: context.isDemoDevice,
  formStatus: context.formStatus,
  documentStatus: context.documentStatus,
  subscriptionTier: context.subscriptionPlan,
  processStatus: context.assistantStatus,
  processType: context.processType,
});

export const createRoadmapState = (context: RoadmapContext): RoadmapState => ({
  completedStages: context.completedStages,
  completedCards: context.completedCards,
  deviceType: context.deviceType,
  isDemo: context.isDemoDevice,
  currentStage: context.currentStage,
  subscriptionTier: context.subscriptionPlan,
});

export const createTaskState = (context: TaskContext): TaskState => ({
  cardKey: context.cardKey,
  stageKey: context.stageKey,
  cardTitle: context.cardTitle,
});

export const createTerminalState = (
  context: TerminalContext
): TerminalState => ({
  threadId: context.threadId,
  deviceId: context.deviceId,
  orgId: context.orgId,
});

// Generic error boundary configuration
export interface ErrorBoundaryConfig {
  componentName: string;
  errorBoundaryName: string;
  componentType: string;
  title: string;
  description: string;
  buttonText: string;
}

// Error boundary component types
export type ErrorBoundaryComponent<
  T extends CommonContextProps = CommonContextProps,
> = (props: BaseErrorBoundaryProps & T) => JSX.Element;
